import { deleteVendor, listOfVendor } from "Api/Vendor";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NameKey,
  NewLabel,
} from "Components/constants/Role";
import {
  ContactNoKey,
  EmailKey,
  EmailLabel,
  PhoneNumberLabel,
  userDraftIdLabel,
} from "Components/constants/Staff";
import {
  organization,
  organizationKey,
  vendorIdLabel,
  vendorLabel,
  vendorName,
} from "Components/constants/Vendor";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Vendor = () => {
  document.title = vendorLabel;
  const navigate = useNavigate();
  const [vendorData, setVendorData] = useState<Array<any>>([]);
  const [staffSearchData, setVendorSearchData] = useState<Array<any>>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number>();
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleDelete = (id: string) => {
    setDeleteOpen(!deleteOpen);
    setDeleteId(Number(id));
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const vendorColumns = useMemo(
    () => [
      {
        header: vendorIdLabel,
        accessorKey: userDraftIdLabel,
        cell: (cell: any) => cell.getValue(),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: vendorName,
        accessorKey: NameKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: organization,
        accessorKey: organizationKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: PhoneNumberLabel,
        accessorKey: ContactNoKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: EmailLabel,
        accessorKey: EmailKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action flex-wrap gap-2 icon">
            <span className="tooltip-container">
              <i
                className={`${
                  !cell?.row?.original?.status ? "icon-disabled" : ""
                } mdi mdi-pencil-outline fs-4 text-info cursor-pointer`}
                onClick={() =>
                  navigate(`/editvendor/${cell?.row?.original?.id}`)
                }
              ></i>
              <span className="tooltip-text">{editLabel}</span>
            </span>

            {/* delete future purpose */}
            {/* <span className="tooltip-container">
              <i
                className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                onClick={() => handleDelete(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{DeleteLabel}</span>
            </span> */}
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  const deleteVendorData = () => {
    setBtnLoader(true);
    deleteVendor(Number(deleteId))
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => {
        setBtnLoader(false);
        setDeleteOpen(!deleteOpen);
        fetchVendorData();
      });
  };

  const fetchVendorData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      type_of_user: vendorLabel,
      limit: customPageSize,
    };
    listOfVendor(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setVendorData(res?.data?.positions);
          setTotalRecords(res?.data?.totalItems);
          setTotalPages(res?.data?.totalPages);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          if (searchValue) {
            fetchSearchVendorData();
          }
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchSearchVendorData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      type_of_user: vendorLabel,
      search: searchValue,
      limit: customPageSize,
    };
    listOfVendor(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setVendorSearchData(response.data.positions);
          setTotalRecords(response.data.totalItems);
          setTotalPages(response?.data?.totalPages);
          setTotalNumberOfRows(response?.data?.numberOfRows);
        }
      })
      .catch((error) => {
        setVendorSearchData([]);
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValue) {
      fetchSearchVendorData();
    } else {
      fetchVendorData();
    }
  }, [currentPage, customPageSize, sortOrder, columnName, searchValue]);

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{vendorLabel}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/addvendor")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4 overflow-x-auto">
          {loader || vendorData.length > 0 ? (
            <TableContainer
              customPageSize={customPageSize}
              setCustomPageSize={setCustomPageSize}
              isGlobalFilter={true}
              columns={vendorColumns}
              data={searchValue ? staffSearchData : vendorData || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
        <DeleteModal
          btnLoader={Boolean(btnLoader)}
          onCloseClick={() => setDeleteOpen(!deleteOpen)}
          onDeleteClick={() => deleteVendorData()}
          show={deleteOpen}
        />
      </div>
    </div>
  );
};

export default Vendor;
